import React from "react";

export const STATUS_LABELS = {
  profile: "Profile needed",
  verify_email: "Email not verified",
  screening: "Screening",
  approved: "Approved",
  pending_review: "Needs review",
  info_requested: "Info requested",
  rejected: "Rejected",
  revoked: "Revoked",
};

export const FLAG_LABELS = {
  competitor_current: "Current competitor employer",
  competitor_domain: "Competitor email domain",
  competitor_ownership: "Competitor ownership",
  competitor_history: "Former competitor employee",
  competitor_possible: "Possible competitor affiliation",
};

export const CONFIDENCE_LABELS = {
  high: "High",
  medium: "Medium",
  low: "Low",
};

export const VERDICT_LABELS = {
  legitimate: "Legitimate",
  unclear: "Unclear",
  bad_actor_suspected: "Bad actor suspected",
};

export function formatTimestamp(value) {
  if (!value) {
    return "—";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return date.toLocaleString();
}

export function safeHttpUrl(value) {
  try {
    const url = new URL(value);

    return url.protocol === "http:" || url.protocol === "https:" ? url.href : null;
  } catch {
    return null;
  }
}

// fetchWithFallback folds the backend body into its error; surface the backend reason when present.
export function errorMessage(error) {
  const reason = String(error?.message || "").match(/"reason":"([^"]+)"/)?.[1];

  return reason || error?.message || "Something went wrong. Please try again.";
}

export function StatusBadge({ status }) {
  return <span className={`ref-badge da-badge-${status}`}>{STATUS_LABELS[status] || status}</span>;
}

export function ConfidenceBadge({ confidence }) {
  if (!confidence) {
    return <span className="ref-muted">—</span>;
  }

  return <span className={`ref-badge da-confidence-${confidence}`}>{CONFIDENCE_LABELS[confidence] || confidence}</span>;
}

export function VerdictBadge({ verdict }) {
  if (!verdict) {
    return <span className="ref-muted">—</span>;
  }

  return <span className={`ref-badge da-verdict-${verdict}`}>{VERDICT_LABELS[verdict] || verdict}</span>;
}

export function ExternalLink({ url, children }) {
  const href = safeHttpUrl(url);
  if (!href) {
    return <span>{children || url}</span>;
  }

  return (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children || url}
    </a>
  );
}

export function ConfirmModal({ action, isBusy, onConfirm, onCancel }) {
  const [text, setText] = React.useState("");

  React.useEffect(() => {
    setText("");
  }, [action]);

  if (!action) {
    return null;
  }

  const isMissingRequiredText = action.textRequired && !text.trim();

  return (
    <div className="ref-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="da-modal-title">
      <div className="ref-modal">
        <h3 id="da-modal-title" className="ref-modal-title">{action.title}</h3>
        <p className="ref-modal-description">{action.description}</p>
        {action.textLabel ? (
          <label className="ref-modal-label">
            {action.textLabel}
            <textarea
              className="ref-modal-textarea"
              maxLength={1000}
              value={text}
              placeholder={action.textPlaceholder}
              onChange={(event) => setText(event.target.value)}
            />
          </label>
        ) : null}
        <div className="ref-modal-actions">
          <button type="button" className="ref-btn ref-btn-ghost" onClick={onCancel} disabled={isBusy}>
            Cancel
          </button>
          <button
            type="button"
            className={`ref-btn ${action.tone === "danger" ? "ref-btn-danger" : "ref-btn-primary"}`}
            onClick={() => onConfirm({ text: text.trim() })}
            disabled={isBusy || isMissingRequiredText}
          >
            {isBusy ? "Working…" : action.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
